import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  SupportRequestFormData,
  THIRD_PARTY_REASONS,
  THIRD_PARTY_RELATIONSHIPS,
} from '../schemas/supportRequestFormSchema';
import { SearchableSelect } from '@/pages/newSupported/components/LabeledInput';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import useBanks from '@/hooks/useBanks';
import useFundContext from '@/contexts/Fund/useFundContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { ThirdPartyPayeeDtoIn } from '@/models/in/ThirdPartyPayeeDtoIn';
import { ThirdPartyPaymentEligibilityDtoIn } from '@/models/in/ThirdPartyPaymentEligibilityDtoIn';
import infoIcon from '@/assets/icons/info2.svg';
import addIcon from '@/assets/icons/add.svg';
import FileUpload from '@/pages/newSupported/components/FileUpload';

const formatCurrency = (value: number) => new Intl.NumberFormat('he-IL').format(value) + ' ₪';

interface ThirdPartyPaymentSectionProps {
  /** The beneficiary this request is for. The section is not rendered without one. */
  supportedId: number | null;
  /** The month the first payment lands in, so a monthly ceiling is measured in the right one. */
  requestedForDate?: string;
}

/**
 * "התשלום מיועד לגורם אחר" inside the ordinary support-request form (HAT-1259).
 *
 * Moshe Klein asked for exactly this placement on 2026-09-23 - not a separate screen: "בעת שליחת
 * בקשת התמיכה צריך לאפשר לרכז לאפשר בקשה להעברה לצד ג'". The request keeps its goal and its
 * amount; only the destination of the money changes.
 *
 * The section renders only when the fund holds an active authorization for this beneficiary. That
 * is a convenience, not the control: the server re-checks the authorization on submit, so nothing
 * here is what permits a payment.
 */
const ThirdPartyPaymentSection = ({ supportedId, requestedForDate }: ThirdPartyPaymentSectionProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const { watch, setValue, control } = useFormContext<SupportRequestFormData>();

  const isThirdPartyPayment = watch('isThirdPartyPayment');
  const thirdPartyPayeeId = watch('thirdPartyPayeeId');
  const thirdPartyReason = watch('thirdPartyReason');
  // Memoised, not `watch(...) ?? []`: the fallback builds a fresh array on every render, which
  // would re-run the memo and the effects below each time anything in the form changes.
  const watchedAdditionalDocuments = watch('additionalDocuments');
  const additionalDocuments = useMemo(
    () => watchedAdditionalDocuments ?? [],
    [watchedAdditionalDocuments]
  );

  // Documents added here go into the same additionalDocuments list the documents step uses, and
  // are told apart by this description - which is also what the office sees next to the file.
  const thirdPartyDocumentDescription = t('thirdPartyPayment.documentDescription');

  const thirdPartyDocumentIndexes = useMemo(
    () => additionalDocuments
      .map((document, index) => ({ document, index }))
      .filter(({ document }) => document.customDescription === thirdPartyDocumentDescription)
      .map(({ index }) => index),
    [additionalDocuments, thirdPartyDocumentDescription]
  );

  const eligibilityUrl = useMemo(() => {
    if (!currentFund?.id || !supportedId) return '';
    const base = `funds/${currentFund.id}/supporteds/${supportedId}/third-party-payment-eligibility`;
    const dateOnly = requestedForDate?.split('T')[0];
    return dateOnly ? `${base}?requestedForDate=${dateOnly}` : base;
  }, [currentFund?.id, supportedId, requestedForDate]);

  const { data: eligibility } = useGetAuthApi<ThirdPartyPaymentEligibilityDtoIn>(eligibilityUrl);

  const { data: savedPayees } = useGetAuthApi<ThirdPartyPayeeDtoIn[]>(
    eligibility?.isAllowed && currentFund?.id ? `funds/${currentFund.id}/third-party-payees` : ''
  );

  const selectedPayee = useMemo(
    () => (thirdPartyPayeeId ? savedPayees?.find((p) => p.id === thirdPartyPayeeId) : undefined),
    [thirdPartyPayeeId, savedPayees]
  );

  // Clearing on un-tick matters: a coordinator who ticks the box, fills a payee, then changes
  // their mind would otherwise submit a request still carrying payee details.
  useEffect(() => {
    if (isThirdPartyPayment) return;
    setValue('thirdPartyPayeeId', null);
    setValue('thirdPartyReason', null);
    setValue('thirdPartyReasonDetails', null);
    setValue('thirdPartyPayeeName', null);
    setValue('thirdPartyPayeeIsraeliId', null);
    setValue('thirdPartyRelationship', null);
    setValue('thirdPartyBankCode', null);
    setValue('thirdPartyBankBranch', null);
    setValue('thirdPartyBankAccount', null);
    // Documents added in this section go with it - otherwise un-ticking the box would leave a
    // seizure order attached to an ordinary request.
    setValue('additionalDocuments', additionalDocuments.filter(
      (document) => document.customDescription !== thirdPartyDocumentDescription
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThirdPartyPayment, setValue]);

  // The whole section disappears if the authorization lapses while the form is open. Leaving the
  // flag set would send a request the server is bound to refuse.
  useEffect(() => {
    if (eligibility && !eligibility.isAllowed && isThirdPartyPayment) {
      setValue('isThirdPartyPayment', false);
    }
  }, [eligibility, isThirdPartyPayment, setValue]);

  // Picking a saved payee fills its details so the coordinator can see what they chose; the server
  // reads them from the stored row, not from these fields.
  useEffect(() => {
    if (!selectedPayee) return;
    setValue('thirdPartyPayeeName', selectedPayee.name);
    setValue('thirdPartyPayeeIsraeliId', selectedPayee.israeliId);
    setValue('thirdPartyRelationship', selectedPayee.relationship);
    setValue('thirdPartyBankCode', selectedPayee.bankCode);
    setValue('thirdPartyBankBranch', selectedPayee.bankBranch ?? '');
    setValue('thirdPartyBankAccount', selectedPayee.bankAccount ?? '');
  }, [selectedPayee, setValue]);

  // A refusal the office recorded is worth showing even though the section stays closed - Moshe
  // Klein asked for the coordinator to see why (2026-09-23, answer 8).
  if (eligibility && !eligibility.isAllowed) {
    if (!eligibility.rejectionReason) return null;
    return (
      <div className="flex items-start gap-2 bg-(--surface-secondary-lowest) rounded-lg p-3">
        <img src={infoIcon} alt="" className="mt-0.5" />
        <span className="body-sm-regular text-(--font-body-1)">
          {t('thirdPartyPayment.refusedByOffice', { reason: eligibility.rejectionReason })}
        </span>
      </div>
    );
  }

  if (!eligibility?.isAllowed) return null;

  const payeeFieldsLocked = !!thirdPartyPayeeId;
  const payeeIdLockedByAuthorization = !!eligibility.restrictedToPayeeIsraeliId;

  return (
    <div className="flex flex-col gap-4 border-t border-(--border-main) pt-6 mt-6">
      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox
          checked={isThirdPartyPayment}
          onCheckedChange={(checked: boolean) => setValue('isThirdPartyPayment', checked)}
        />
        <span className="body-lr-bold text-(--font-sub-title-2)">{t('thirdPartyPayment.toggle')}</span>
      </label>

      {!isThirdPartyPayment && (
        <p className="body-sm-regular text-(--font-secondary)">{t('thirdPartyPayment.toggleHint')}</p>
      )}

      {isThirdPartyPayment && (
        <>
          <div className="flex items-start gap-2 bg-(--surface-secondary-lowest) rounded-lg p-3">
            <img src={infoIcon} alt="" className="mt-0.5" />
            <div className="flex flex-col gap-1">
              <span className="body-sm-regular text-(--font-body-1)">{t('thirdPartyPayment.noticeNeedsApproval')}</span>
              {eligibility.remainingAmount != null && (
                <span className="body-sm-regular text-(--font-secondary)">
                  {t('thirdPartyPayment.remainingAmount', {
                    amount: formatCurrency(eligibility.remainingAmount),
                    scope: t(`thirdPartyPayment.limitScope.${eligibility.limitScope}`),
                  })}
                </span>
              )}
            </div>
          </div>

          {/* Reason */}
          <div className="flex flex-col gap-2">
            <span className="body-sm-semibold text-(--font-secondary)">{t('thirdPartyPayment.reasonLabel')}</span>
            <div className="flex flex-wrap gap-2">
              {THIRD_PARTY_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => setValue('thirdPartyReason', reason)}
                  className={`px-4 py-2 rounded-lg border body-sm-regular transition-colors ${
                    thirdPartyReason === reason
                      ? 'border-(--border-input-default) bg-(--surface-secondary-lowest) body-sm-semibold'
                      : 'border-(--border-main) bg-(--surface-base)'
                  }`}
                >
                  {t(`thirdPartyPayment.reason.${reason}`)}
                </button>
              ))}
            </div>
            <p className="body-sm-regular text-(--font-secondary)">
              {t(`thirdPartyPayment.requiredDocument.${thirdPartyReason ?? 'none'}`)}
            </p>
          </div>

          {/* The office's transfer form puts "העלאת מסמכים רלוונטיים" inside this section, and it
              has to be here rather than in the documents step: that step comes BEFORE this one, so
              by the time the coordinator learns which document the reason needs, they have already
              passed it. These append to the request's additionalDocuments, so there is still only
              one path documents travel to the server. */}
          <div className="flex flex-col gap-2">
            <span className="body-sm-semibold text-(--font-secondary)">
              {t('thirdPartyPayment.uploadDocuments')}
            </span>

            {thirdPartyDocumentIndexes.map((documentIndex) => (
              <FileUpload
                key={documentIndex}
                placeholder={t('thirdPartyPayment.uploadDocuments')}
                value={additionalDocuments[documentIndex]?.file ?? null}
                onChange={(newFile) => {
                  const updated = [...additionalDocuments];
                  updated[documentIndex] = {
                    file: newFile,
                    customDescription: thirdPartyDocumentDescription,
                  };
                  setValue('additionalDocuments', updated);
                }}
                customDescription={{
                  description: additionalDocuments[documentIndex]?.file?.name ?? '',
                  onDescriptionChange: () => {},
                  onRemove: () => setValue(
                    'additionalDocuments',
                    additionalDocuments.filter((_, index) => index !== documentIndex)
                  ),
                }}
              />
            ))}

            <button
              type="button"
              onClick={() => setValue('additionalDocuments', [
                ...additionalDocuments,
                { file: null, customDescription: thirdPartyDocumentDescription },
              ])}
              className="bg-(--surface-base) border border-(--border-main) rounded-lg h-9 px-3 flex items-center justify-center gap-1 body-sm-semibold text-(--font-primary) self-start"
            >
              <img src={addIcon} alt="" />
              {t('thirdPartyPayment.addDocument')}
            </button>
          </div>

          {thirdPartyReason === 'Other' && (
            <div className="flex flex-col gap-1 max-w-[560px]">
              <label htmlFor="third-party-reason-details" className="body-sm-semibold text-(--font-secondary)">
                {t('thirdPartyPayment.reasonDetails')}
              </label>
              <Controller
                control={control}
                name="thirdPartyReasonDetails"
                render={({ field }) => (
                  <textarea
                    id="third-party-reason-details"
                    className="border border-(--border-input-default) rounded-md px-3 py-2 body-md-regular resize-none"
                    rows={2}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
          )}

          {/* Saved payees */}
          {savedPayees && savedPayees.length > 0 && (
            <div className="flex flex-col gap-1 max-w-[402px]">
              <label htmlFor="third-party-saved-payee" className="body-sm-semibold text-(--font-secondary)">
                {t('thirdPartyPayment.savedPayee')}
              </label>
              <select
                id="third-party-saved-payee"
                className="border border-(--border-input-default) rounded-md p-3 body-md-regular"
                value={thirdPartyPayeeId ?? 0}
                onChange={(e) => {
                  const picked = Number(e.target.value);
                  setValue('thirdPartyPayeeId', picked > 0 ? picked : null);
                  if (picked === 0) {
                    setValue('thirdPartyPayeeName', null);
                    setValue('thirdPartyPayeeIsraeliId', null);
                    setValue('thirdPartyRelationship', null);
                    setValue('thirdPartyBankCode', null);
                    setValue('thirdPartyBankBranch', null);
                    setValue('thirdPartyBankAccount', null);
                  }
                }}
              >
                <option value={0}>{t('thirdPartyPayment.newPayee')}</option>
                {savedPayees.map((payee) => (
                  <option key={payee.id} value={payee.id}>
                    {payee.name} ({payee.israeliId})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Payee identity */}
          <div className="grid grid-cols-3 max-md:grid-cols-1 gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="third-party-payee-name" className="body-sm-semibold text-(--font-secondary)">
                {t('thirdPartyPayment.payeeName')}
              </label>
              <Controller
                control={control}
                name="thirdPartyPayeeName"
                render={({ field }) => (
                  <input
                    id="third-party-payee-name"
                    type="text"
                    className="border border-(--border-input-default) rounded-md p-3 body-md-regular disabled:bg-(--surface-secondary-lowest)"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder={t('thirdPartyPayment.payeeNamePlaceholder')}
                    disabled={payeeFieldsLocked}
                  />
                )}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="third-party-relationship" className="body-sm-semibold text-(--font-secondary)">
                {t('thirdPartyPayment.relationship')}
              </label>
              <Controller
                control={control}
                name="thirdPartyRelationship"
                render={({ field }) => (
                  <select
                    id="third-party-relationship"
                    className="border border-(--border-input-default) rounded-md p-3 body-md-regular disabled:bg-(--surface-secondary-lowest)"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    disabled={payeeFieldsLocked}
                  >
                    <option value="">{t('thirdPartyPayment.relationshipPlaceholder')}</option>
                    {THIRD_PARTY_RELATIONSHIPS.map((relationship) => (
                      <option key={relationship} value={relationship}>
                        {t(`thirdPartyPayment.relationshipOption.${relationship}`)}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="third-party-payee-id" className="body-sm-semibold text-(--font-secondary)">
                {t('thirdPartyPayment.payeeIsraeliId')}
              </label>
              <Controller
                control={control}
                name="thirdPartyPayeeIsraeliId"
                render={({ field }) => (
                  <input
                    id="third-party-payee-id"
                    type="text"
                    dir="ltr"
                    className="border border-(--border-input-default) rounded-md p-3 body-md-regular disabled:bg-(--surface-secondary-lowest)"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder={t('thirdPartyPayment.payeeIsraeliIdPlaceholder')}
                    disabled={payeeFieldsLocked}
                  />
                )}
              />
              {payeeIdLockedByAuthorization && (
                <span className="body-sm-regular text-(--font-secondary)">
                  {t('thirdPartyPayment.restrictedToPayee', { id: eligibility.restrictedToPayeeIsraeliId })}
                </span>
              )}
            </div>
          </div>

          {/* Payee bank account */}
          <div className="grid grid-cols-3 max-md:grid-cols-1 gap-4">
            <Controller
              control={control}
              name="thirdPartyBankCode"
              render={({ field }) => (
                <SearchableSelect
                  label={t('bankCode')}
                  width="100%"
                  value={field.value != null ? String(field.value) : ''}
                  onChange={(value) => {
                    const bankNumber = parseInt(value.split(' - ')[0]);
                    field.onChange(isNaN(bankNumber) ? null : bankNumber);
                  }}
                  loadOptions={useBanks.getBankOptions}
                  placeholder="bankCodePlaceholder"
                  required
                  disabled={payeeFieldsLocked}
                />
              )}
            />

            <div className="flex flex-col gap-1">
              <label htmlFor="third-party-bank-account" className="body-sm-semibold text-(--font-secondary)">
                {t('bankAccount')}
              </label>
              <Controller
                control={control}
                name="thirdPartyBankAccount"
                render={({ field }) => (
                  <input
                    id="third-party-bank-account"
                    type="text"
                    dir="ltr"
                    className="border border-(--border-input-default) rounded-md p-3 body-md-regular disabled:bg-(--surface-secondary-lowest)"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    disabled={payeeFieldsLocked}
                  />
                )}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="third-party-bank-branch" className="body-sm-semibold text-(--font-secondary)">
                {t('bankBranch')}
              </label>
              <Controller
                control={control}
                name="thirdPartyBankBranch"
                render={({ field }) => (
                  <input
                    id="third-party-bank-branch"
                    type="text"
                    dir="ltr"
                    className="border border-(--border-input-default) rounded-md p-3 body-md-regular disabled:bg-(--surface-secondary-lowest)"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder={t('thirdPartyPayment.bankBranchPlaceholder')}
                    disabled={payeeFieldsLocked}
                  />
                )}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThirdPartyPaymentSection;
